import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import clsx from 'clsx';
import { useUi, useAssetCdn } from '@/context/Ui';
import { Lines, SymbolType, CdnObjectType } from '@/types';
import { useSounds } from '@/context/LessonPlayer';
import { findSoundsInString } from '@/utils/sounds';
import { CanvasWrite, AudioPlayer } from '@/components';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { WhitePanel } from '../components/WhitePanel';
import { Toolbar } from '../components/Toolbar';
import { TouchCounter } from '../components/TouchCounter';
import { Underlines } from '../components/Underlines';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';

interface WriteWordInputProps {
  word: string;
  showAnswer: boolean;
  hasPlayedAudioOnce: boolean;
  setHasPlayedAudioOnce: (hasPlayedAudioOnce: boolean) => void;
}

interface WriteWordInput {
  asBitmap: () => string | undefined;
  asVector: () => Lines | undefined;
}

const WriteWordInput = forwardRef<WriteWordInput, WriteWordInputProps>(
  (
    {
      word,
      showAnswer = false,
      hasPlayedAudioOnce = false,
      setHasPlayedAudioOnce,
    },
    ref,
  ) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const writeRef = useRef<CanvasWrite>(null);
    const playerRef = useRef<AudioPlayer>(null);

    const { sendAlert } = useUi();

    const mediaUrl = useAssetCdn(word, SymbolType.Word, CdnObjectType.Audio);

    useImperativeHandle(ref, () => ({
      asBitmap: () => writeRef.current?.asBitmap(),
      asVector: () => writeRef.current?.asVector(),
    }));

    const sounds = useSounds();

    const [playerReady, setPlayerReady] = useState<boolean>(false);

    const [soundCount, setSoundCount] = useState<number>(0);

    const [canvasShowing, setCanvasShowing] = useState<boolean>(false);
    const [canvasWidth, setCanvasWidth] = useState<number>(768);

    const [hideSoundCountButton, setHideSoundCountButton] =
      useState<boolean>(false);

    const wordSounds = findSoundsInString(
      sounds.map((sound) => sound.name),
      word,
    );

    const getLetterWidthClass = () => {
      switch (wordSounds.length) {
        case 1:
        case 2:
        case 3:
          return 'w-1/3';
        case 4:
          return 'w-1/4';
        case 5:
          return 'w-1/6';
        case 6:
          return 'w-[14.28%]';
        case 7:
          return 'w-[12.5%]';
        case 8:
          return 'w-[11.11%]';
      }

      return '';
    };

    useEffect(() => {
      if (!panelRef.current) {
        return;
      }

      const panel = panelRef.current;

      const handleResize = () => {
        const { width } = panel.getBoundingClientRect();
        setCanvasWidth((width > 500 ? 500 : width) - 48);
      };

      window.addEventListener('resize', handleResize);

      handleResize();

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    useEffect(() => {
      if (soundCount < wordSounds.length) {
        return;
      }

      setCanvasShowing(true);

      const HIDE_TIMEOUT = 200;

      const timeout = setTimeout(() => {
        setHideSoundCountButton(true);
      }, HIDE_TIMEOUT);

      return () => clearTimeout(timeout);
    }, [soundCount, wordSounds]);

    return (
      <>
        <Toolbar
          onPlay={() => {
            playerRef.current?.play();

            setHasPlayedAudioOnce(true);
          }}
          playDisabled={!playerReady}
          onRefresh={
            canvasShowing ? () => writeRef.current?.reset() : undefined
          }
          hintPlay={!hasPlayedAudioOnce}
        />

        <WhitePanel ref={panelRef} mode="basic" kind="fill" color="paper">
          {hasPlayedAudioOnce && (
            <div className="flex flex-col gap-6 items-center">
              <TouchCounter
                className={clsx(
                  'transition-all duration-500 ease-in-out',
                  hideSoundCountButton && 'opacity-0',
                )}
                buttonClassName={clsx(
                  'transition-all duration-500 ease-in-out',
                  canvasShowing && 'cursor-not-allowed opacity-0',
                  hideSoundCountButton && 'opacity-0',
                )}
                count={soundCount}
                setCount={setSoundCount}
                limit={wordSounds.length}
              />

              <div
                className={`flex flex-col gap-12 relative h-96`}
                style={{
                  width: `${canvasWidth}px`,
                }}
              >
                <CanvasWrite
                  ref={writeRef}
                  className={clsx(
                    `w-full max-w-[${canvasWidth}px] h-96 mx-auto opacity-0`,
                    'transition-all duration-300 ease-in-out',
                    canvasShowing ? 'opacity-100' : 'opacity-0',
                  )}
                  drawWidth={canvasWidth}
                  drawHeight={384}
                />

                <Underlines
                  length={soundCount}
                  wordSoundsLength={wordSounds.length}
                  letterWidthClass={getLetterWidthClass()}
                />

                <p
                  className={clsx(
                    'text-black text-6xl font-bold text-center',
                    'transition-all duration-600 ease-in-out font-sassoonInfant',
                    showAnswer ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  {word}
                </p>
              </div>
            </div>
          )}

          <AudioPlayer
            ref={playerRef}
            src={mediaUrl || ''}
            onReadySrc={() => setPlayerReady(true)}
            onLoadTimeoutSrc={() =>
              sendAlert(
                'We could not load some of the audio for this lesson.',
                {
                  type: 'warning',
                  title: 'There was a problem',
                },
              )
            }
          />
        </WhitePanel>
      </>
    );
  },
);

WriteWordInput.displayName = 'WriteWordInput';

interface WriteWordStepProps extends ActivityTypeProps {
  index: number;
  total: number;
  word: string;
}

const WriteWordStep = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
  index,
  total,
  word,
}: WriteWordStepProps) => {
  const writeWordInputRef = useRef<WriteWordInput | null>(null);

  const [showAnswer, setShowAnswer] = useState<boolean>(false);

  const [hasPlayedAudioOnce, setHasPlayedAudioOnce] = useState<boolean>(false);

  return (
    <ActivityStep
      key={`${activity.id}-${index}-1`}
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      usePanel
      title={`Listen to the word. Tap the number of sounds and write the letters.`}
      showTitle={mode === 'TEACH'}
      showActivityPagination={false}
      onBeforeLeave={() => {
        if (!hasPlayedAudioOnce) {
          return true;
        }

        if (!showAnswer) {
          setShowAnswer(true);

          return false;
        }

        return true;
      }}
      getActivityResponseMetaData={() => ({
        text: word,
        base64Image: writeWordInputRef.current?.asBitmap(),
        points: JSON.stringify(writeWordInputRef.current?.asVector()),
      })}
    >
      <WriteWordInput
        ref={writeWordInputRef}
        word={word}
        showAnswer={showAnswer}
        hasPlayedAudioOnce={hasPlayedAudioOnce}
        setHasPlayedAudioOnce={setHasPlayedAudioOnce}
      />
    </ActivityStep>
  );
};

export const SpellingWriteWords = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words) {
    throw new Error('Words are required for this activity');
  }

  const { words } = activity;

  return (
    <Activity
      steps={[
        ...(mode === 'TEACH'
          ? [
              <ActivityTitleStep
                key={`${activity.id}-title`}
                activity={activity}
                mode={mode}
                wrapperClassName={wrapperClassName}
                isPreview={isPreview}
                index={0}
                total={words.length + 1}
                parrotType="desk"
              />,
            ]
          : []),
        ...words.flatMap((word, index) => [
          <WriteWordStep
            key={`${activity.id}-${index}-1`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={index + 1}
            total={words.length + 1}
            word={word}
          />,
        ]),
      ]}
    />
  );
};
